import { useEffect, useMemo, useState } from "react";
import {
  siListGeo, siCreateGeo, siUpdateGeo, siDeleteGeo, siReorderGeo, siSeedGeo,
  siListRegionDeviceCosts, siSaveRegionDeviceCosts,
} from "../lib/smartInspectApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, Globe, RefreshCw } from "lucide-react";

const EMPTY = {
  country_name: "", country_code: "", currency_name: "", currency_code: "",
  currency_symbol: "", pricing_region: "", display_order: 0,
  active: true, show_in_smartinspect: true,
};

function Badge({ on, onText = "Active", offText = "Inactive" }) {
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-semibold ${on ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#F3F4F6] text-[#9CA3AF]"}`}>
      {on ? onText : offText}
    </span>
  );
}

export default function SmartInspectCountryCurrencyPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deviceCosts, setDeviceCosts] = useState([]);
  const [savingDevice, setSavingDevice] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [geo, dc] = await Promise.all([siListGeo(), siListRegionDeviceCosts()]);
      setRows(geo);
      setDeviceCosts(dc || []);
    }
    catch { toast.error("Failed to load country/currency records"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Unique active Pricing Regions (with currency) derived from the country rows.
  const regionRows = useMemo(() => {
    const seen = {};
    [...rows]
      .filter((g) => g.active && (g.pricing_region || "").trim())
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .forEach((g) => {
        const r = g.pricing_region;
        if (!seen[r]) seen[r] = { pricing_region: r, currency_code: g.currency_code || "", currency_symbol: g.currency_symbol || "" };
      });
    return Object.values(seen);
  }, [rows]);

  const deviceCostFor = (region) => {
    const found = (deviceCosts || []).find((d) => d.pricing_region === region);
    return found && found.device_cost != null ? found.device_cost : "";
  };
  const paperCostFor = (region) => {
    const found = (deviceCosts || []).find((d) => d.pricing_region === region);
    return found && found.paper_cost_per_inspection != null ? found.paper_cost_per_inspection : "";
  };
  const storageBenchFor = (region) => {
    const found = (deviceCosts || []).find((d) => d.pricing_region === region);
    return found && found.storage_it_benchmark != null ? found.storage_it_benchmark : "";
  };
  const updateBenchmark = (region, currency_code, field, value) => {
    setDeviceCosts((prev) => {
      const existing = prev.find((d) => d.pricing_region === region) || { pricing_region: region, currency_code };
      const others = prev.filter((d) => d.pricing_region !== region);
      return [...others, { ...existing, pricing_region: region, currency_code, [field]: value === "" ? "" : Number(value) }];
    });
  };
  const saveDeviceCosts = async () => {
    setSavingDevice(true);
    try {
      const payload = regionRows.map((r) => ({
        pricing_region: r.pricing_region,
        currency_code: r.currency_code,
        device_cost: deviceCostFor(r.pricing_region) === "" ? 0 : Number(deviceCostFor(r.pricing_region)),
        paper_cost_per_inspection: paperCostFor(r.pricing_region) === "" ? 0 : Number(paperCostFor(r.pricing_region)),
        storage_it_benchmark: storageBenchFor(r.pricing_region) === "" ? 0 : Number(storageBenchFor(r.pricing_region)),
      }));
      const saved = await siSaveRegionDeviceCosts(payload);
      setDeviceCosts(saved || []);
      toast.success("Regional benchmarks saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingDevice(false); }
  };

  const sorted = useMemo(
    () => [...rows].sort((a, b) => (a.display_order || 0) - (b.display_order || 0)),
    [rows]
  );

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= sorted.length) return;
    const a = sorted[idx], b = sorted[j];
    const ao = a.display_order || 0, bo = b.display_order || 0;
    setRows((prev) => prev.map((x) => x.id === a.id ? { ...x, display_order: bo } : x.id === b.id ? { ...x, display_order: ao } : x));
    try { await siReorderGeo([{ id: a.id, display_order: bo }, { id: b.id, display_order: ao }]); }
    catch { toast.error("Reorder failed"); load(); }
  };

  const toggle = async (r, field) => {
    try {
      const updated = await siUpdateGeo(r.id, { ...r, [field]: !r[field] });
      setRows((prev) => prev.map((x) => (x.id === r.id ? updated : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const openNew = () => setEditing({ ...EMPTY, display_order: (sorted.length || 0) + 1 });

  const save = async () => {
    if (!editing.country_name.trim()) { toast.error("Country name is required."); return; }
    setSaving(true);
    try {
      const payload = { ...editing, display_order: Number(editing.display_order) || 0 };
      if (editing.id) {
        const u = await siUpdateGeo(editing.id, payload);
        setRows((prev) => prev.map((x) => (x.id === editing.id ? u : x)));
        toast.success("Record updated");
      } else {
        const c = await siCreateGeo(payload);
        setRows((prev) => [...prev, c]);
        toast.success("Record created");
      }
      setEditing(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await siDeleteGeo(deleteTarget.id);
      setRows((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
    finally { setDeleteTarget(null); }
  };

  const reseed = async () => {
    try { const r = await siSeedGeo(); toast.success(`Seeded ${r.inserted || 0} default record(s)`); load(); }
    catch { toast.error("Seed failed"); }
  };

  const setF = (patch) => setEditing((e) => ({ ...e, ...patch }));

  return (
    <div data-testid="si-geo-page">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>Countries, Currencies & Regional Benchmarks</h1>
        <div className="flex items-center gap-2">
          <Button data-testid="si-geo-seed-btn" onClick={reseed} variant="outline" className="rounded-sm text-xs">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Seed Defaults
          </Button>
          <Button data-testid="si-geo-add-btn" onClick={openNew} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Country
          </Button>
        </div>
      </div>
      <p className="text-xs text-[#9CA3AF] mb-4 max-w-3xl leading-relaxed">
        Admin-managed list that drives the SmartInspect ROI Calculator Step 1 Country and Preferred Currency dropdowns.
        Country, Currency and Pricing Region are linked here. Pricing Region is internal and never shown to public users.
      </p>

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-[#025979]" /></div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden" data-testid="si-geo-table">
          <div className="px-4 py-3 bg-gray-50 border-b border-[#E5E7EB] flex items-center justify-between">
            <p className="text-xs font-medium text-[#111827] flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-[#025979]" /> Country / Currency Records</p>
            <p className="text-[10px] text-[#9CA3AF]">{sorted.length} record{sorted.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#E5E7EB]">
                  <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-16">Order</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-medium min-w-[180px]">Country</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-40">Currency</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-32">Pricing Region</th>
                  <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-16">Active</th>
                  <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-28">In SmartInspect</th>
                  <th className="text-right px-3 py-2 text-[#6B7280] font-medium w-24">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="si-geo-tbody">
                {sorted.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-[#9CA3AF]">No records yet. Click “Add Country” or “Seed Defaults”.</td></tr>
                ) : sorted.map((r, idx) => (
                  <tr key={r.id} data-testid={`si-geo-row-${r.country_code || idx}`} className="border-b border-[#E5E7EB] hover:bg-gray-50 align-middle">
                    <td className="px-2 py-2.5">
                      <div className="flex items-center justify-center gap-0.5">
                        <span className="text-[#9CA3AF] text-[10px] w-4 text-center">{idx + 1}</span>
                        <div className="flex flex-col">
                          <button data-testid={`si-geo-moveup-${r.country_code}`} onClick={() => move(idx, -1)} disabled={idx === 0} className="text-[#6B7280] hover:text-[#025979] disabled:text-[#E5E7EB]"><ArrowUp className="w-3 h-3" /></button>
                          <button data-testid={`si-geo-movedown-${r.country_code}`} onClick={() => move(idx, 1)} disabled={idx === sorted.length - 1} className="text-[#6B7280] hover:text-[#025979] disabled:text-[#E5E7EB]"><ArrowDown className="w-3 h-3" /></button>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-[#111827]">
                      <div className="font-medium">{r.country_name}</div>
                      <div className="text-[9px] text-[#9CA3AF] font-mono mt-0.5">{r.country_code}</div>
                    </td>
                    <td className="px-3 py-2.5 text-[10px] text-[#6B7280]">
                      <span className="font-mono font-semibold text-[#111827]">{r.currency_code}</span> — {r.currency_name}
                      {r.currency_symbol ? <span className="text-[#9CA3AF]"> ({r.currency_symbol})</span> : null}
                    </td>
                    <td className="px-3 py-2.5 text-[10px] text-[#6B7280]">{r.pricing_region || "—"}</td>
                    <td className="px-2 py-2.5 text-center">
                      <button data-testid={`si-geo-toggle-active-${r.country_code}`} onClick={() => toggle(r, "active")} title="Activate / Deactivate"><Badge on={!!r.active} /></button>
                    </td>
                    <td className="px-2 py-2.5 text-center">
                      <button data-testid={`si-geo-toggle-show-${r.country_code}`} onClick={() => toggle(r, "show_in_smartinspect")} title="Show in SmartInspect"><Badge on={!!r.show_in_smartinspect} onText="Shown" offText="Hidden" /></button>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-2">
                        <button data-testid={`si-geo-edit-${r.country_code}`} onClick={() => setEditing({ ...r })} className="text-[#6B7280] hover:text-[#025979]"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`si-geo-delete-${r.country_code}`} onClick={() => setDeleteTarget(r)} className="text-[#6B7280] hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Regional Money Benchmarks (Sprint 8Q + 8S) */}
      {!loading && (
        <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden mt-6" data-testid="si-device-cost-card">
          <div className="px-4 py-3 bg-gray-50 border-b border-[#E5E7EB] flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-[#111827] flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-[#025979]" /> SmartInspect Regional Benchmarks by Pricing Region</p>
              <p className="text-[10px] text-[#9CA3AF] mt-0.5">Local-currency regional defaults read by the SmartInspect ROI engine — Device unit cost, Paper cost per inspection and Storage & IT benchmark (default when the customer leaves Storage & IT blank). No exchange-rate conversion.</p>
            </div>
            <Button data-testid="si-device-cost-save-btn" onClick={saveDeviceCosts} disabled={savingDevice} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs">
              {savingDevice ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save Benchmarks"}
            </Button>
          </div>
          {regionRows.length === 0 ? (
            <div className="px-4 py-8 text-center text-[#9CA3AF] text-xs">No active Pricing Regions yet. Add countries with a Pricing Region above.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#E5E7EB]">
                    <th className="text-left px-4 py-2 text-[#6B7280] font-medium">Pricing Region</th>
                    <th className="text-left px-4 py-2 text-[#6B7280] font-medium w-28">Currency</th>
                    <th className="text-left px-4 py-2 text-[#6B7280] font-medium w-52">Device Cost</th>
                    <th className="text-left px-4 py-2 text-[#6B7280] font-medium w-52">Paper Cost / Inspection</th>
                    <th className="text-left px-4 py-2 text-[#6B7280] font-medium w-52">Storage & IT Benchmark</th>
                  </tr>
                </thead>
                <tbody data-testid="si-device-cost-tbody">
                  {regionRows.map((r) => (
                    <tr key={r.pricing_region} data-testid={`si-device-cost-row-${r.pricing_region}`} className="border-b border-[#E5E7EB] hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-[#111827]">{r.pricing_region}</td>
                      <td className="px-4 py-2.5 text-[#6B7280] font-mono">{r.currency_code || "—"}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {r.currency_symbol ? <span className="text-[#9CA3AF]">{r.currency_symbol}</span> : null}
                          <Input
                            data-testid={`si-device-cost-input-${r.pricing_region}`}
                            type="number" min="0" step="1"
                            value={deviceCostFor(r.pricing_region)}
                            onChange={(e) => updateBenchmark(r.pricing_region, r.currency_code, "device_cost", e.target.value)}
                            className="rounded-sm text-xs h-8 max-w-[150px]"
                            placeholder="0"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {r.currency_symbol ? <span className="text-[#9CA3AF]">{r.currency_symbol}</span> : null}
                          <Input
                            data-testid={`si-paper-cost-input-${r.pricing_region}`}
                            type="number" min="0" step="0.01"
                            value={paperCostFor(r.pricing_region)}
                            onChange={(e) => updateBenchmark(r.pricing_region, r.currency_code, "paper_cost_per_inspection", e.target.value)}
                            className="rounded-sm text-xs h-8 max-w-[150px]"
                            placeholder="0.00"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {r.currency_symbol ? <span className="text-[#9CA3AF]">{r.currency_symbol}</span> : null}
                          <Input
                            data-testid={`si-storage-bench-input-${r.pricing_region}`}
                            type="number" min="0" step="1"
                            value={storageBenchFor(r.pricing_region)}
                            onChange={(e) => updateBenchmark(r.pricing_region, r.currency_code, "storage_it_benchmark", e.target.value)}
                            className="rounded-sm text-xs h-8 max-w-[150px]"
                            placeholder="0"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Editor dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="si-geo-editor">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit Country" : "Add Country"}</DialogTitle>
            <DialogDescription>Configure the country, its currency and internal pricing region.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Country Name *</label>
                <Input data-testid="si-geo-field-country-name" value={editing.country_name} onChange={(e) => setF({ country_name: e.target.value })} placeholder="e.g. United States" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Country Code</label>
                <Input data-testid="si-geo-field-country-code" value={editing.country_code} onChange={(e) => setF({ country_code: e.target.value })} placeholder="US" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Pricing Region</label>
                <Input data-testid="si-geo-field-pricing-region" value={editing.pricing_region} onChange={(e) => setF({ pricing_region: e.target.value })} placeholder="USA" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Currency Code</label>
                <Input data-testid="si-geo-field-currency-code" value={editing.currency_code} onChange={(e) => setF({ currency_code: e.target.value })} placeholder="USD" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Currency Symbol</label>
                <Input data-testid="si-geo-field-currency-symbol" value={editing.currency_symbol} onChange={(e) => setF({ currency_symbol: e.target.value })} placeholder="$" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Currency Name</label>
                <Input data-testid="si-geo-field-currency-name" value={editing.currency_name} onChange={(e) => setF({ currency_name: e.target.value })} placeholder="US Dollar" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Display Order</label>
                <Input data-testid="si-geo-field-order" type="number" value={editing.display_order} onChange={(e) => setF({ display_order: e.target.value })} />
              </div>
              <div className="flex items-center gap-6 col-span-2 pt-1">
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <Switch data-testid="si-geo-field-active" checked={!!editing.active} onCheckedChange={(v) => setF({ active: v })} /> Active
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <Switch data-testid="si-geo-field-show" checked={!!editing.show_in_smartinspect} onCheckedChange={(v) => setF({ show_in_smartinspect: v })} /> Show in SmartInspect
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-sm text-xs">Cancel</Button>
            <Button data-testid="si-geo-save-btn" onClick={save} disabled={saving} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="si-geo-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this country?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleteTarget?.country_name}” will be removed from the SmartInspect dropdowns. Existing assessments keep their stored values.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="si-geo-confirm-delete" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 rounded-sm text-xs">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
